const MAX_ITEMS = 80;
const PREVIEW_COUNT = 5;

const storageKey = (userId) =>
  `vitra_web_push_history_v1_${userId != null ? String(userId) : 'anon'}`;

const normalizeItem = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id: String(id),
    title: raw.title || 'Notification',
    body: raw.body || '',
    notificationType: raw.notificationType || raw.notification_type || '',
    url: raw.url || '/messages',
    sessionName: raw.sessionName || raw.session_name || '',
    appointmentId: raw.appointmentId || raw.appointment_id || null,
    conversationId: raw.conversationId || raw.conversation_id || null,
    messageId: raw.messageId || raw.message_id || null,
    patientMrn: raw.patientMrn || raw.patient_mrn || null,
    receivedAt: raw.receivedAt || new Date().toISOString(),
    read: Boolean(raw.read),
  };
};

const typeOf = (item) => String(item?.notificationType || '').toLowerCase();

const isChatItem = (item) => {
  const t = typeOf(item);
  return (
    t === 'chat_message' ||
    t === 'message' ||
    t === 'new_message' ||
    Boolean(item?.conversationId)
  );
};

const isAppointmentFamily = (item) => {
  const t = typeOf(item);
  return (
    t.startsWith('appointment_') ||
    t.startsWith('zoom_') ||
    t === 'video_waiting_room' ||
    t === 'video_patient_left_call' ||
    Boolean(item?.appointmentId) ||
    Boolean(item?.sessionName)
  );
};

export function notificationStackKey(item) {
  const normalized = normalizeItem(item);
  if (!normalized) return null;

  if (isChatItem(normalized)) {
    if (normalized.conversationId != null && String(normalized.conversationId).trim() !== '') {
      return `chat:conv:${normalized.conversationId}`;
    }
    if (normalized.patientMrn != null && String(normalized.patientMrn).trim() !== '') {
      return `chat:mrn:${normalized.patientMrn}`;
    }
    const titleKey = String(normalized.title || '')
      .toLowerCase()
      .replace(/^new message from\s+/i, '')
      .trim();
    if (titleKey) return `chat:title:${titleKey}`;
  }

  if (isAppointmentFamily(normalized)) {
    if (normalized.appointmentId != null && String(normalized.appointmentId).trim() !== '') {
      return `appt:${normalized.appointmentId}`;
    }
    if (normalized.sessionName) {
      return `session:${normalized.sessionName}`;
    }
  }

  if (typeOf(normalized) === 'new_alert' && normalized.patientMrn) {
    return `alert:mrn:${normalized.patientMrn}`;
  }

  if (typeOf(normalized) === 'patient_feeling' && normalized.patientMrn) {
    return `feeling:mrn:${normalized.patientMrn}`;
  }

  return `single:${normalized.id}`;
}

function stackKindFromKey(key) {
  if (!key) return 'single';
  if (key.startsWith('chat:')) return 'chat';
  if (key.startsWith('appt:') || key.startsWith('session:')) return 'appointment';
  if (key.startsWith('alert:')) return 'alert';
  if (key.startsWith('feeling:')) return 'feeling';
  return 'single';
}

function summarizeStack(kind, items) {
  const latest = items[0];
  const count = items.length;

  if (kind === 'chat') {
    const who = String(latest.title || 'Message')
      .replace(/^new message from\s+/i, '')
      .trim() || 'Messages';
    return {
      title: who,
      subtitle:
        count > 1
          ? `${count} messages · ${latest.body || 'New activity'}`
          : latest.body || 'New message',
      typeLabel: count > 1 ? `${count} messages` : 'Message',
    };
  }

  if (kind === 'appointment') {
    return {
      title: latest.title || 'Appointment',
      subtitle:
        count > 1
          ? `${count} updates · ${latest.body || notificationTypeFallback(latest)}`
          : latest.body || notificationTypeFallback(latest),
      typeLabel: count > 1 ? `${count} appointment updates` : notificationTypeFallback(latest),
    };
  }

  if (kind === 'alert') {
    return {
      title: latest.title || 'Patient alert',
      subtitle:
        count > 1
          ? `${count} alerts · ${latest.body || 'Needs review'}`
          : latest.body || 'Needs review',
      typeLabel: count > 1 ? `${count} alerts` : 'Alert',
    };
  }

  if (kind === 'feeling') {
    return {
      title: latest.title || 'Patient feeling',
      subtitle:
        count > 1
          ? `${count} updates · ${latest.body || ''}`
          : latest.body || 'Patient feeling update',
      typeLabel: count > 1 ? `${count} feeling updates` : 'Patient feeling',
    };
  }

  return {
    title: latest.title || 'Notification',
    subtitle: latest.body || '',
    typeLabel: notificationTypeFallback(latest),
  };
}

function notificationTypeFallback(item) {
  const t = typeOf(item);
  if (!t) return 'Notification';
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function groupPushNotifications(items) {
  const list = (Array.isArray(items) ? items : []).map(normalizeItem).filter(Boolean);
  const buckets = new Map();

  for (const item of list) {
    const key = notificationStackKey(item);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  }

  const stacks = [];
  for (const [key, bucket] of buckets.entries()) {
    const sorted = [...bucket].sort(
      (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
    );
    const kind = stackKindFromKey(key);
    const summary = summarizeStack(kind, sorted);
    const unreadCount = sorted.filter((i) => !i.read).length;
    stacks.push({
      key,
      kind,
      items: sorted,
      latest: sorted[0],
      count: sorted.length,
      unreadCount,
      read: unreadCount === 0,
      receivedAt: sorted[0].receivedAt,
      title: summary.title,
      subtitle: summary.subtitle,
      typeLabel: summary.typeLabel,
    });
  }

  stacks.sort(
    (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  );
  return stacks;
}

export function loadPushNotificationHistory(userId) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeItem).filter(Boolean).slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

export function savePushNotificationHistory(userId, items) {
  if (typeof window === 'undefined') return;
  try {
    const cleaned = (Array.isArray(items) ? items : [])
      .map(normalizeItem)
      .filter(Boolean)
      .slice(0, MAX_ITEMS);
    window.localStorage.setItem(storageKey(userId), JSON.stringify(cleaned));
  } catch {
  }
}

export function mergePushNotification(existing, incoming, { isZoomType } = {}) {
  const item = normalizeItem(incoming);
  if (!item) return Array.isArray(existing) ? existing : [];
  const prev = Array.isArray(existing) ? existing : [];
  const isZoom =
    typeof isZoomType === 'function' ? isZoomType(item) : false;

  if (isZoom && item.sessionName) {
    const existingIdx = prev.findIndex(
      (p) =>
        p?.sessionName &&
        p.sessionName === item.sessionName &&
        String(p?.notificationType || '') === String(item.notificationType || '')
    );
    if (existingIdx >= 0) {
      const next = [...prev];
      next[existingIdx] = { ...next[existingIdx], ...item };
      return next.slice(0, MAX_ITEMS);
    }
  }

  if (item.messageId != null && String(item.messageId).trim() !== '') {
    const msgId = String(item.messageId);
    const existingIdx = prev.findIndex(
      (p) => p?.messageId != null && String(p.messageId) === msgId
    );
    if (existingIdx >= 0) {
      const next = [...prev];
      next[existingIdx] = { ...next[existingIdx], ...item, id: next[existingIdx].id };
      return next.slice(0, MAX_ITEMS);
    }
  }

  const deduped = prev.filter((p) => p.id !== item.id);
  return [item, ...deduped].slice(0, MAX_ITEMS);
}

export function markPushNotificationsRead(items, ids = null) {
  const list = Array.isArray(items) ? items : [];
  if (ids == null) {
    return list.map((item) => ({ ...item, read: true }));
  }
  const idSet = new Set((Array.isArray(ids) ? ids : [ids]).map(String));
  return list.map((item) =>
    idSet.has(String(item.id)) ? { ...item, read: true } : item
  );
}

export function unreadPushCount(items) {
  return (Array.isArray(items) ? items : []).filter((item) => !item.read).length;
}

export const PUSH_HISTORY_PREVIEW_COUNT = PREVIEW_COUNT;
export const PUSH_HISTORY_MAX_ITEMS = MAX_ITEMS;
